import React, { useState, useEffect, useCallback } from 'react'
import { X, Calendar, User, Clock, AlertTriangle, ChevronDown } from 'lucide-react'
import { employeeService } from '../../services/employeeService'
import branchService from '../../services/branchService'
import { leaveTypeService } from '../../services/leaveService'

const EditLeaveModal = ({ isOpen, onClose, onSubmit, leave }) => {
  const [formData, setFormData] = useState({
    employee: '',
    branch: '',
    leaveType: '',
    startDate: '',
    endDate: '',
    isHalfDay: false,
    status: 'pending',
    reason: ''
  })

  const [employees, setEmployees] = useState([])
  const [branches, setBranches] = useState([])
  const [leaveTypes, setLeaveTypes] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [errors, setErrors] = useState({})

  const fetchDropdownData = useCallback(async () => {
    try {
      setLoadingData(true)
      
      const [employeesRes, branchesRes, leaveTypesRes] = await Promise.all([
        employeeService.getAllEmployees(),
        branchService.getAllBranches(),
        leaveTypeService.getAllLeaveTypes()
      ])

      // Handle employees response
      if (employeesRes.success) {
        const employeesList = employeesRes.data?.employees || employeesRes.data || []
        setEmployees(employeesList.filter(emp => emp.isActive))
      }

      // Handle branches response
      if (branchesRes.success) {
        const branchesList = branchesRes.data?.branches || branchesRes.data || []
        setBranches(branchesList.filter(branch => branch.isActive))
      }

      // Handle leave types response
      if (leaveTypesRes.success) {
        const leaveTypesList = leaveTypesRes.data?.leaveTypes || leaveTypesRes.data || []
        setLeaveTypes(leaveTypesList.filter(type => type.isActive))
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error)
    } finally {
      setLoadingData(false)
    }
  }, [])

  const populateFormData = useCallback(() => {
    if (leave) {
      const formatDate = (dateString) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return date.toISOString().split('T')[0]
      }

      setFormData({
        employee: leave.employee?._id || leave.employee || '',
        branch: leave.branch?._id || leave.branch || '',
        leaveType: leave.leaveType?._id || leave.leaveType || '',
        startDate: formatDate(leave.startDate),
        endDate: formatDate(leave.endDate),
        isHalfDay: Boolean(leave.isHalfDay),
        status: leave.status || 'pending',
        reason: leave.reason || ''
      })
    }
  }, [leave])

  useEffect(() => {
    if (isOpen && leave) {
      fetchDropdownData()
      populateFormData()
    }
  }, [isOpen, leave, fetchDropdownData, populateFormData])

  const resetForm = () => {
    setFormData({
      employee: '',
      branch: '',
      leaveType: '',
      startDate: '',
      endDate: '',
      isHalfDay: false,
      status: 'pending',
      reason: ''
    })
    setErrors({})
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name === 'isHalfDay' && checked) {
      // If half day is selected, set end date to start date
      setFormData(prev => ({
        ...prev,
        [name]: checked,
        endDate: prev.startDate
      }))
    } else if (name === 'startDate' && formData.isHalfDay) {
      // If start date changes and it's a half day, update end date too
      setFormData(prev => ({
        ...prev,
        [name]: value,
        endDate: value
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }))
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.employee) newErrors.employee = 'Employee is required'
    if (!formData.branch) newErrors.branch = 'Branch is required'
    if (!formData.leaveType) newErrors.leaveType = 'Leave type is required'
    if (!formData.startDate) newErrors.startDate = 'Start date is required'
    if (!formData.endDate) newErrors.endDate = 'End date is required'

    // Date validation
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate)
      const endDate = new Date(formData.endDate)
      
      if (endDate < startDate) {
        newErrors.endDate = 'End date must be after or equal to start date'
      }

      // Half day validation
      if (formData.isHalfDay && startDate.getTime() !== endDate.getTime()) {
        newErrors.endDate = 'For half day leave, start and end dates must be the same'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return

    try {
      setLoading(true)
      
      // Only send fields that are allowed to be updated according to API specification
      const submitData = {
        leaveType: formData.leaveType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        isHalfDay: formData.isHalfDay
      }

      // Only include reason if it's provided and if API supports it
      if (formData.reason.trim()) {
        submitData.reason = formData.reason.trim()
      }

      // Only include status if it's different from original (optional field)
      if (formData.status !== leave.status) {
        submitData.status = formData.status
      }

      await onSubmit(leave._id, submitData)
      handleClose()
    } catch (error) {
      console.error('Error updating leave:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  if (!isOpen) return null

  const getEmployeeName = () => {
    if (leave?.employee?.fullName) return leave.employee.fullName
    const employee = employees.find(emp => emp._id === formData.employee)
    return employee?.fullName || 'Unknown'
  }

  const getBranchName = () => {
    if (leave?.branch?.name) return leave.branch.name
    const branch = branches.find(br => br._id === formData.branch)
    return branch?.name || 'Unknown'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-gray-200">
          <div className="flex items-center space-x-3">
            {/* <div className="bg-blue-100 p-2 rounded-full">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div> */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Edit Leave</h2>
              <p className="text-sm text-gray-600">
                {getEmployeeName()} - {getBranchName()}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {loadingData ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading form data...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Employee Selection (Read-only in edit mode) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employee *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={getEmployeeName()}
                    readOnly
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Employee cannot be changed</p>
              </div>

              {/* Leave Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Leave Type *
                </label>
                <div className="relative">
                  <select
                    name="leaveType"
                    value={formData.leaveType}
                    onChange={handleInputChange}
                    className={`w-full px-4 pr-8 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white ${
                      errors.leaveType ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading}
                  >
                    <option value="">Select leave type</option>
                    {leaveTypes.map(type => (
                      <option key={type._id} value={type._id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                {errors.leaveType && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    {errors.leaveType}
                  </p>
                )}
              </div>
            </div>

            {/* Branch Selection (Read-only in edit mode) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Branch *
              </label>
              <input
                type="text"
                value={getBranchName()}
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
              />
              <p className="mt-1 text-xs text-gray-500">Branch cannot be changed</p>
            </div>

            {/* Date Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.startDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                />
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    {errors.startDate}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  disabled={formData.isHalfDay || loading}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.endDate ? 'border-red-500' : 'border-gray-300'
                  } ${formData.isHalfDay ? 'bg-gray-100' : ''}`}
                />
                {errors.endDate && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    {errors.endDate}
                  </p>
                )}
              </div>
            </div>

            {/* Half Day and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isHalfDay"
                  name="isHalfDay"
                  checked={formData.isHalfDay}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isHalfDay" className="text-sm font-medium text-gray-700 flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  Half Day Leave
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <div className="relative">
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-4 pr-8 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                    disabled={loading}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (Optional)
              </label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                rows={3}
                placeholder="Enter reason for leave..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  'Update Leave'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default EditLeaveModal
